import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AddDomainRequest,
    DomainResponse,
    DomainsService,
    RegisterDomainRequest
} from '@app/modules/domains/domains.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

export type DomainWizardStep =
  | 'select_type'
  | 'enter_domain'
  | 'domain_purchase'
  | 'select_dns_management'
  | 'configure_dns'
  | 'verification'
  | 'complete';

export const DomainWizardStep = {
  SELECT_TYPE: 'select_type' as DomainWizardStep,
  ENTER_DOMAIN: 'enter_domain' as DomainWizardStep,
  DOMAIN_PURCHASE: 'domain_purchase' as DomainWizardStep,
  SELECT_DNS_MANAGEMENT: 'select_dns_management' as DomainWizardStep,
  CONFIGURE_DNS: 'configure_dns' as DomainWizardStep,
  VERIFICATION: 'verification' as DomainWizardStep,
  COMPLETE: 'complete' as DomainWizardStep,
};

interface DomainSuggestion {
  name: string;
  price: number;
  available: boolean;
}

interface ContactInfo {
  name: string;
  address: string;
  city: string;
  country: string;
  phone: string;
}

interface SearchError {
  message: string;
  suggestion: string;
}

@Component({
  selector: 'sbnb-domains-wizard',
  templateUrl: './domains-wizard.component.html',
  styleUrls: ['./domains-wizard.component.scss'],
})
export class DomainsWizardComponent implements OnInit {
  DomainWizardStep = DomainWizardStep;
  currentStep: DomainWizardStep = DomainWizardStep.SELECT_TYPE;
  domainType: 'connect' | 'purchase' = 'connect';
  dnsManagement: 'hospitable' | 'self' = 'hospitable';
  domainForm: FormGroup;
  domainName = '';
  pendingDomainName = '';
  domainUuid: string | null = null;
  siteId: string | null = null;
  closeLink = '/settings/domains';

  // Domain purchase related properties
  domainSearchQuery = '';
  searchResults: DomainSuggestion[] = [];
  searchError: SearchError | null = null;
  selectedDomain: DomainSuggestion = { name: '', price: 0, available: false };
  autoRenew = true;
  termsAccepted = false;
  isLoading = false;

  contactInfo: ContactInfo = {
    name: 'Abby Grant',
    address: '123 Main St',
    city: 'St Petersburg',
    country: 'United States',
    phone: '7345604569',
  };

  nameServers: string[] = [
    'ns1.hsptbl.com',
    'ns2.hsptbl.com',
    'ns3.hsptbl.com',
    'ns4.hsptbl.com'
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private domainsService: DomainsService,
    private toast: ToastNotificationsService
  ) {
    this.domainForm = this.fb.group({
      domainName: [
        '',
        [Validators.required, Validators.pattern(/^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/)],
      ],
    });
  }

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId');
    if (this.siteId) {
      this.closeLink = `/direct/sites/${this.siteId}/domains`;
    }
  }

  selectDomainType(type: 'connect' | 'purchase'): void {
    if (type === 'purchase') {
      return;
    }

    this.domainType = type;
    this.currentStep = DomainWizardStep.ENTER_DOMAIN;
  }

  selectDnsManagement(type: 'hospitable' | 'self'): void {
    this.dnsManagement = type;
  }

  searchDomains(): void {
    if (!this.domainSearchQuery) return;

    this.searchResults = [];
    this.searchError = null;
    this.isLoading = true;

    this.domainsService
      .checkDomainAvailability(this.domainSearchQuery)
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          console.error('Error checking domain availability:', error);
          this.toast.error('Error checking domain availability. Please try again.');
          return of(false);
        })
      )
      .subscribe((available) => {
        if (available) {
          this.domainsService
            .getDomainPricing(this.domainSearchQuery)
            .pipe(
              catchError((error) => {
                console.error('Error getting domain pricing:', error);
                return of({
                  domain: this.domainSearchQuery,
                  premium: false,
                  registration_price: 15.99,
                  renewal_price: 15.99,
                  transfer_price: 0,
                });
              })
            )
            .subscribe((pricing) => {
              this.searchResults = [
                {
                  name: this.domainSearchQuery,
                  price: pricing.registration_price,
                  available: true,
                },
              ];
            });
        } else {
          this.searchError = {
            message: `${this.domainSearchQuery} isn't available`,
            suggestion: 'Please try a different domain name.',
          };
          this.searchResults = [];
        }
      });
  }

  selectDomainToPurchase(domain: DomainSuggestion): void {
    this.selectedDomain = domain;
    this.domainName = domain.name;
    this.currentStep = DomainWizardStep.DOMAIN_PURCHASE;
  }

  editContactInfo(): void {
    this.toast.success('Contact information editing would open here');
  }

  purchaseDomain(): void {
    if (!this.termsAccepted) return;

    this.isLoading = true;

    const request: RegisterDomainRequest = {
      domain_name: this.selectedDomain.name,
      registrant_id: 1,
      auto_renew: this.autoRenew,
      whois_privacy: true,
    };

    this.domainsService
      .registerDomain(request)
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          console.error('Error registering domain:', error);
          this.toast.error('Error registering domain. Please try again.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toast.success(`Domain ${this.selectedDomain.name} purchased successfully!`);
          this.currentStep = DomainWizardStep.SELECT_DNS_MANAGEMENT;
        }
      });
  }

  submitDomainData(): Observable<DomainResponse> {
    if (!this.domainName) {
      this.toast.error('Domain name is missing.');
      return throwError(() => new Error('Domain name is missing'));
    }

    this.isLoading = true;
    const request: AddDomainRequest = {
      domain_name: this.domainName,
      dns_managed: this.dnsManagement === 'hospitable',
    };
    if (this.siteId) {
      request.site_id = this.siteId;
    }

    return this.domainsService.addExternalDomain(request).pipe(
      tap((response) => {
        this.domainUuid = response.data.domain.uuid;
      }),
      catchError((error) => {
        const errorMessage = error.status === 409
           ? `Domain ${this.domainName} already exists or could not be added.`
           : (error?.error?.message || 'Error submitting domain. Please try again.');
        this.toast.error(errorMessage);
        console.error('Error submitting domain:', error);
        return throwError(() => error);
      }),
      finalize(() => (this.isLoading = false))
    );
  }

  private handleDomainCheck(): void {
    if (!this.domainForm.valid) {
       this.domainForm.markAllAsTouched();
       return;
    }
    this.domainName = this.domainForm.get('domainName')?.value.toLowerCase().trim();
    this.isLoading = true;

    this.domainsService.listDomains().pipe(
      map(domains => domains.find(d => d.name === this.domainName))
    ).subscribe(existingDomain => {
      if (existingDomain) {
        this.dnsManagement = existingDomain.dnsManaged ? 'hospitable' : 'self';
        this.submitDomainData().subscribe({
          next: () => {
            this.currentStep = DomainWizardStep.COMPLETE;
          },
          error: () => {
            // Error is handled (including setting isLoading false) in submitDomainData
          },
        });
      } else {
        this.isLoading = false;
        this.currentStep = DomainWizardStep.SELECT_DNS_MANAGEMENT;
      }
    });
  }

  continue(): void {
    switch (this.currentStep) {
      case DomainWizardStep.SELECT_TYPE:
        this.currentStep = DomainWizardStep.ENTER_DOMAIN;
        break;
      case DomainWizardStep.ENTER_DOMAIN:
        this.handleDomainCheck();
        break;
      case DomainWizardStep.SELECT_DNS_MANAGEMENT:
        this.submitDomainData().subscribe({
          next: () => {
            this.currentStep = DomainWizardStep.COMPLETE;
          },
          error: () => {
            // Error handled in submitDomainData
          },
        });
        break;
      case DomainWizardStep.COMPLETE:
        if (this.siteId) {
          this.router.navigate(['/direct/sites', this.siteId, 'domains']);
        } else {
          this.router.navigate(['/settings/domains']);
        }
        break;
      default:
        this.currentStep = DomainWizardStep.SELECT_TYPE;
        break;
    }
  }

  goBack(): void {
    switch (this.currentStep) {
      case DomainWizardStep.ENTER_DOMAIN:
        this.currentStep = DomainWizardStep.SELECT_TYPE;
        break;
      case DomainWizardStep.DOMAIN_PURCHASE:
        this.currentStep = DomainWizardStep.ENTER_DOMAIN;
        break;
      case DomainWizardStep.SELECT_DNS_MANAGEMENT:
        this.currentStep =
          this.domainType === 'connect' ? DomainWizardStep.ENTER_DOMAIN : DomainWizardStep.DOMAIN_PURCHASE;
        break;
      case DomainWizardStep.COMPLETE:
        this.currentStep = DomainWizardStep.SELECT_DNS_MANAGEMENT;
        break;
    }
  }

  cancel(): void {
    this.router.navigate(['/direct/domains']);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.success('Copied to clipboard');
    });
  }

  copyNameserver(index: number): void {
    this.copyToClipboard(this.nameServers[index]);
  }

  copyAllNameservers(): void {
    this.copyToClipboard(this.nameServers.join('\n'));
  }
}
